import { isArray, isNotEmpty, replaceEmpty } from '@lhb/func';
import { FC, useMemo } from 'react';
import { checkboxCheckIsRed, checkNumberInputIsRed, checkRadioIsRed, decodeTextValue } from '../../config';
import { ControlType, OTHER_TYPE_ID, parseValueCatch } from '@/common/components/business/DynamicComponent/config';
import { Col, Row } from 'antd';
import V2DetailItem from '@/common/components/Feedback/V2DetailItem';
import styles from './index.module.less';

/**
 * 动态表单-子表单详情
 * @Description 机会点导出有复用部分逻辑，尽量保持一致 src/views/pdf/pages/chancepointreport/components/Detail/SubForm.tsx
 */
const SubForm:FC<{
  /** 子表单名称 */
  label,
  /** 子表单值 */
  value,
  /** 子表单模版list */
  configList,
  /** 页面提示语 data?.infoModule?.hint */
  hintStr,
  /** 是否显示页面提示语 */
  setHintStr,
  detailInfoConfig,
}> = ({ label, value, configList, hintStr, setHintStr, detailInfoConfig = { span: 12 } }) => {

  /** 配置项映射表 */
  const configListMap = (Array.isArray(configList) ? configList : []).reduce((result, item) => {
    result[item.id] = item;
    return result;
  }, {});

  // 按 row 分组排序
  const valueList = useMemo(() => {
    const values = value ? JSON.parse(value) : [];
    const grouped = (Array.isArray(values) ? values : []).reduce((result, item) => {
      const row = item.row;
      if (!result[row]) {
        result[row] = [];
      }
      // 将配置项合并到 item 中，使其能拿到该项标题
      const _item = Object.assign({}, configListMap?.[item.id] || {}, item);
      result[row].push(_item);
      return result;
    }, []);
    console.log('valueList', grouped, configListMap);
    return grouped;
  }, [value, configListMap]);

  return (
    isArray(valueList) && valueList.length ? <div className={styles.subForm}>
      {valueList?.map((item, index) => {
        return <div key={index}>
          <div className={styles.subFormTitle}>{label}{index ? index + 1 : ''}</div>
          {Array.isArray(item) && item.length ? <div className={styles.subFormContent}>
            <Row key={index} gutter={24}>
              {item?.map((itm, idx) =>
                <PropertyItem key={`${index}-${idx}`} item={itm} hintStr={hintStr} setHintStr={setHintStr} detailInfoConfig={detailInfoConfig} />
              )}
            </Row>
          </div> : <>-</>}
        </div>;
      })}
    </div> : <></>
  );
};

/** 获取动态表单子表单的详情项标题（取自 formConfigList） */
export const getSubformDeailItemLabel = (item: { anotherName: any; name: any; }) => item ? (item.anotherName || item.name) : '';

/** 获取动态表单子表单的详情项值 */
export const getSubformDeailItemValue = (item: {
  controlType: any,
  textValue: any
}) => {
  if (!item) {
    return null;
  }
  const { controlType, textValue } = item;
  let detailItemValue = '';
  switch (controlType) {
    // 单选
    case ControlType.SINGLE_RADIO.value:{
      detailItemValue = textValue?.id === OTHER_TYPE_ID
        ? `${textValue.name}(${textValue?.input})`
        : textValue?.name;
      break;
    }
    // 多选
    case ControlType.CHECK_BOX.value:{
      detailItemValue = textValue?.map((value) => value?.id === OTHER_TYPE_ID
        ? `${value.name}(${value?.input || ''})`
        : value?.name).join('、');
      break;
    }
    // 数字输入框
    case ControlType.INPUT_NUMBER.value:
      detailItemValue = `${isNotEmpty(textValue?.value) ? textValue?.value : '-'}${textValue?.suffix || ''}`;
      break;
    // 省市区
    case ControlType.AREA.value:
    case ControlType.CUSTOM_AREA.value:
      detailItemValue = (Array.isArray(textValue) ? textValue : []).reduce((acc, item) => {
        // 省市区中文去重
        return acc.includes(item.name) ? acc : acc.concat(item.name);
      }, []).join('') || '-';
      break;
    default:
      // 文本、多行文本、时间等
      detailItemValue = textValue || '-';
      break;
  }
  return replaceEmpty(detailItemValue);
};

/**
 * 属性展示项
 */
const PropertyItem:FC<{
  /** 子表单配置项 */
  item: any,
  /** 页面提示语 data?.infoModule?.hint */
  hintStr,
  /** 是否显示页面提示语 */
  setHintStr,
  detailInfoConfig,
}> = ({ item, hintStr, setHintStr, detailInfoConfig = { span: 12 } }) => {

  /* state */
  if (!item) {
    return null;
  }
  const { controlType } = item || {};
  const textValue = decodeTextValue(controlType, item.textValue) || null;
  const label = getSubformDeailItemLabel(item);
  const value = getSubformDeailItemValue({ controlType, textValue });

  const { templateRestriction } = item;
  let templateRestrictionVal: any = {};
  if (templateRestriction) {
    templateRestrictionVal = parseValueCatch(item, 'templateRestriction');
  }
  const { redMark } = templateRestrictionVal;
  // 是否是换行显示
  const { nextLine } = templateRestrictionVal;
  let colorVal = '';

  console.log('subFormItem', item);

  /* hooks */

  /* methods */
  switch (controlType) {
    // 单选
    case ControlType.SINGLE_RADIO.value:
      const targetOption = checkRadioIsRed(templateRestrictionVal, textValue?.id);
      // 注意这里有坑，decodeTextValue中把selectedId转成了id
      colorVal = targetOption ? '#f23030' : '';
      colorVal && targetOption?.isPageTip && setHintStr && hintStr && setHintStr(hintStr);
      return (<>
        {nextLine ? <Col span={24}></Col> : null}
        <Col {...detailInfoConfig}>
          <V2DetailItem
            labelStyle={{ color: colorVal }}
            valueStyle={{ color: colorVal }}
            verticalMiddleHelp={<>
              {colorVal && targetOption?.isItemTip && targetOption?.itemTip
                ? <div className='c-f23 fs-12'>{targetOption?.itemTip}</div>
                : null}
            </>}
            label={label}
            value={value}/>
        </Col>
      </>);
      // 多选
    case ControlType.CHECK_BOX.value:
      const targetOptions: any[] = checkboxCheckIsRed(templateRestrictionVal, textValue);
      const targetStr = targetOptions?.reduce((acc, cur, curIndex) => {
        const { isRedMark, isItemTip, itemTip } = cur;
        if (isRedMark && isItemTip && itemTip) return `${acc}${itemTip || ''}${curIndex < (targetOptions.length - 1) ? '、' : ''}`;
        return '';
      }, '');
        // 注意这里有坑，decodeTextValue中把textValue中的selectedId转成了id
      colorVal = isArray(targetOptions) && targetOptions.length > 0 ? '#f23030' : '';
      const targetIsPageTip = targetOptions?.find((item) => item.isPageTip);
      colorVal && !!targetIsPageTip && setHintStr && hintStr && setHintStr(hintStr);
      return (<>
        {nextLine ? <Col span={24}></Col> : null}
        <Col {...detailInfoConfig}>
          <V2DetailItem
            labelStyle={{ color: colorVal }}
            valueStyle={{ color: colorVal }}
            verticalMiddleHelp={<>
              {targetStr ? <div className='c-f23 fs-12'>{targetStr}</div> : null}
            </>}
            label={label}
            value={value}/>
        </Col>
      </>);
    case ControlType.TEXT_AREA.value:
      // 多行文本类
      return (<>
        {nextLine ? <Col span={24}></Col> : null }
        <Col {...detailInfoConfig}>
          <V2DetailItem type='textarea' label={label} value={value} />
        </Col>
      </>);
      // 数字，带后缀
    case ControlType.INPUT_NUMBER.value:
      colorVal = checkNumberInputIsRed(templateRestrictionVal, textValue?.value) ? '#f23030' : '';
      colorVal && redMark?.isPageTip && setHintStr && hintStr && setHintStr(hintStr);
      return (<>
        {nextLine ? <Col span={24}></Col> : null}
        <Col {...detailInfoConfig}>
          <V2DetailItem
            labelStyle={{ color: colorVal }}
            valueStyle={{ color: colorVal }}
            label={label}
            verticalMiddleHelp={<>
              {colorVal && redMark?.isItemTip && redMark?.itemTip
                ? <div className='c-f23 fs-12'>{redMark?.itemTip}</div>
                : null}
            </>}
            value={value}/>
        </Col>
      </>);

    default:
      // 文本类、时间等
      return (<>
        {nextLine ? <Col span={24}></Col> : null}
        <Col {...detailInfoConfig}>
          <V2DetailItem label={label} value={value} />
        </Col>
      </>);
  }
};



export default SubForm;
